/**
 * Scroll-out counterpart to Dawn's reveal animations (assets/animations.js).
 *
 * Dawn animates its blocks in and then leaves them alone. This fades them
 * back out, but only once they are about to leave through the top of the
 * window, so nothing disappears while it is still comfortably readable.
 * The effect is tied to the scroll position, so scrolling back down brings
 * the block straight back.
 *
 * Dawn's reveal is a CSS animation with animation-fill-mode: forwards, which
 * outranks plain inline styles — hence setProperty(..., 'important') here.
 * The properties are removed again as soon as the block leaves the zone, so
 * outside it the element is exactly as the theme left it.
 */
(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SELECTOR = '.scroll-trigger.animate--slide-in, .scroll-trigger.animate--fade-in';
  const OFFSCREEN = 'scroll-trigger--offscreen';

  const ZONE = 0.14; /* how much of the window height above the top edge counts as "leaving" */
  const MIN_OPACITY = 0.3;
  const SHIFT = 8; /* px the block drifts up as it goes */
  const CURVE = 1.6; /* > 1 keeps the block at full strength until it is close to the edge */

  const clamp = (value) => (value < 0 ? 0 : value > 1 ? 1 : value);

  class ScrollExit {
    constructor() {
      this.items = [];
      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      this.collect();
      if (!this.items.length) return;

      this.update();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      REDUCED_MOTION.addEventListener('change', this.update);
    }

    /* The footer runs its own reveal (assets/footer-reveal.js) and is skipped. */
    collect() {
      this.items = Array.from(document.querySelectorAll(SELECTOR)).filter(
        (element) => !element.closest('.footer__frame')
      );
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    update() {
      if (REDUCED_MOTION.matches) {
        this.items.forEach((element) => this.clear(element));
        return;
      }

      const zone = Math.max(window.innerHeight * ZONE, 1);

      this.items.forEach((element) => {
        /* Still waiting for its reveal: leave it to Dawn. */
        if (element.classList.contains(OFFSCREEN)) {
          this.clear(element);
          return;
        }

        const bottom = element.getBoundingClientRect().bottom;
        const progress = clamp(1 - bottom / zone);

        if (progress <= 0) {
          this.clear(element);
          return;
        }

        const eased = Math.pow(progress, CURVE);
        const opacity = 1 - (1 - MIN_OPACITY) * eased;

        element.style.setProperty('opacity', opacity.toFixed(3), 'important');
        element.style.setProperty('transform', `translate3d(0, ${(-SHIFT * eased).toFixed(2)}px, 0)`, 'important');
      });
    }

    clear(element) {
      if (!element.style.getPropertyValue('opacity')) return;
      element.style.removeProperty('opacity');
      element.style.removeProperty('transform');
    }
  }

  function init() {
    if (window.scrollExit) {
      window.scrollExit.collect();
      window.scrollExit.update();
      return;
    }
    window.scrollExit = new ScrollExit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
